import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Check from '@mui/icons-material/Check';
import PaletteOutlined from '@mui/icons-material/PaletteOutlined';
import { useThemePreset, useZuulColorMode, type ThemePreset } from '@/theme';
import { derivePalette } from '@/theme/derivePalette';

/**
 * A miniature of the preset: the page background it derives, with the primary and secondary
 * accents sitting on it. A preset changes every surface, not just the accents, so previewing
 * only the accent pair would understate the difference between entries.
 */
function Swatch({ preset, size = 26 }: { preset: ThemePreset; size?: number }) {
  const { mode } = useZuulColorMode();
  const { primary, secondary } = preset[mode];
  const palette = derivePalette(preset[mode], mode);
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
        bgcolor: palette.background!.default,
        boxShadow: `inset 0 0 0 1px ${palette.divider}`,
      }}
    >
      <Box sx={{ width: 7, height: 14, borderRadius: '2px', bgcolor: primary }} />
      <Box sx={{ width: 7, height: 14, borderRadius: '2px', bgcolor: secondary }} />
    </Box>
  );
}

/**
 * Header dropdown to pick a color preset. Each preset has a light and a dark rendition; the mode
 * toggle and the font-size menu are separate buttons beside this one.
 */
export default function ThemePresetMenu() {
  const { presetId, setPresetId, presets, preset } = useThemePreset();
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  return (
    <>
      <Tooltip title={`Theme colors: ${preset.label}`}>
        <IconButton
          aria-label='Theme colors'
          aria-haspopup='menu'
          aria-controls={open ? 'theme-preset-menu' : undefined}
          aria-expanded={open || undefined}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <PaletteOutlined />
        </IconButton>
      </Tooltip>
      <Menu
        id='theme-preset-menu'
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { dense: true, 'aria-label': 'Theme colors' } }}
      >
        <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>Preset color</ListSubheader>
        {presets.map((item) => {
          const selected = item.id === presetId;
          return (
            <MenuItem
              key={item.id}
              selected={selected}
              aria-checked={selected}
              role='menuitemradio'
              onClick={() => {
                setPresetId(item.id);
                setAnchor(null);
              }}
              sx={{ gap: 1.5, pr: 5 }}
            >
              <Swatch preset={item} />
              <ListItemText primary={item.label} />
              {selected && (
                <ListItemIcon sx={{ minWidth: 0, ml: 'auto' }}>
                  <Check fontSize='small' />
                </ListItemIcon>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
